import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MeResponse, SignInRequest, SignInResponse, UpdateMeRequest } from '@sourcito/schemas';
import { apiFetch } from './api';

/**
 * `meQuery` — the authoritative source of "is the current visitor signed in?"
 * Fails (throws ApiCallError 401) when no valid session cookie. Cached for 5min.
 * Used both by useMe() in components and by router beforeLoad guards.
 */
export const meQuery = queryOptions({
  queryKey: ['me'] as const,
  queryFn: () => apiFetch('/api/me', { responseSchema: MeResponse }),
  retry: false,
  staleTime: 5 * 60 * 1000,
});

export function useMe() {
  return useQuery(meQuery);
}

/**
 * M0 entitlements: the tenant's effective module map from /api/me.
 * `useModuleEnabled('orders')` → false while loading or when off; gated nav
 * simply doesn't render until the map arrives.
 */
export function useModuleEnabled(module: string): boolean {
  const { data } = useMe();
  return data?.modules?.[module] === true;
}

/**
 * PATCH /api/me — rename the signed-in user's own profile. Returns the
 * refreshed MeResponse; invalidating `meQuery` (rather than setQueryData)
 * keeps this the single source of truth for "what did the server just say"
 * and is what re-renders the sidebar greeting (AppShell reads useMe()).
 */
export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeRequest) =>
      apiFetch('/api/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: MeResponse,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    },
  });
}

export function useSignin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SignInRequest) =>
      apiFetch('/api/auth/signin', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: SignInResponse,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useSignout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>('/api/auth/signout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['me'] });
      queryClient.clear();
      navigate({ to: '/signin' });
    },
  });
}
